/**
 * Endpoint coverage — compares the CrowdStrike host inventory with the endpoints that run an
 * Agenti5 agent, so it is clear where the agent is installed, silent, or missing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Icon, Modal, StatusPulse } from '../components/ui';
import { usePermission } from '../hooks/usePermission';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../i18n/format';

interface Props {
  apiUrl: string;
}

interface CsConfig {
  cloud: string;
  clientId: string;
  hasSecret: boolean;
  enabled: boolean;
  syncIntervalMin: number;
  lastSyncAt: number | null;
  lastStatus: string | null;
  lastCounts: { hosts: number; pages: number; removed: number } | null;
}

type Coverage = 'installed' | 'silent' | 'missing' | 'inactive';

interface Row {
  coverage: Coverage;
  host: { source_id: string; hostname: string | null; platform: string | null; local_ip: string | null; last_seen: string | null };
  agenti5: { endpoint_id: string; last_seen: string | null; agent_version: string | null } | null;
}

interface Report {
  summary: { total: number; installed: number; silent: number; missing: number; inactive: number; coveragePct: number; lastSyncAt: number | null; agenti5NotInInventory: number };
  rows: Row[];
  unlisted: Array<{ endpoint_id: string; hostname: string | null; os: string | null; last_seen: string | null }>;
}

async function asJson(res: Response): Promise<any> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || `Request failed (${res.status})`);
  return data;
}

const send = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

const INTERVALS: Array<[number, string]> = [[60, 'Every hour'], [360, 'Every 6 hours'], [720, 'Every 12 hours'], [1440, 'Every 24 hours']];

const BADGE: Record<Coverage, string> = { installed: 'badge-success', silent: 'badge-warning', missing: 'badge-error', inactive: '' };

const EndpointCoverage: React.FC<Props> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const canWrite = usePermission('manage:settings');
  const [config, setConfig] = useState<CsConfig | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [clouds, setClouds] = useState<string[]>(['us-1', 'us-2', 'eu-1', 'us-gov-1']);
  const [form, setForm] = useState({ cloud: 'us-1', clientId: '', clientSecret: '', syncIntervalMin: 1440, enabled: true });
  const [report, setReport] = useState<Report | null>(null);
  const [filter, setFilter] = useState<'all' | Coverage>('all');
  const [showSettings, setShowSettings] = useState(false);
  const [testNote, setTestNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const loadReport = useCallback(async () => {
    try {
      setReport(await asJson(await fetch(`${apiUrl}/admin/endpoints/coverage`)));
    } catch {
      /* nothing synced yet */
    }
  }, [apiUrl]);

  const loadConfig = useCallback(async () => {
    try {
      const r = await asJson(await fetch(`${apiUrl}/admin/crowdstrike/config`));
      setConfig(r.config);
      if (Array.isArray(r.clouds) && r.clouds.length) setClouds(r.clouds);
      if (r.config) setForm({ cloud: r.config.cloud, clientId: r.config.clientId, clientSecret: '', syncIntervalMin: r.config.syncIntervalMin, enabled: r.config.enabled });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoaded(true);
    }
  }, [apiUrl]);

  useEffect(() => {
    void loadConfig();
    void loadReport();
  }, [loadConfig, loadReport]);

  const run = async (key: string, fn: () => Promise<string>) => {
    setBusy(key);
    setError(null);
    setNote(null);
    try {
      setNote(await fn());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const save = () =>
    run('save', async () => {
      const r = await asJson(await fetch(`${apiUrl}/admin/crowdstrike/config`, send('PUT', form)));
      setConfig(r.config);
      setForm((f) => ({ ...f, clientSecret: '' }));
      setShowSettings(false);
      return t('pages.endpointCoverage.saved', 'Saved');
    });

  const test = async () => {
    setBusy('test');
    setTestNote(null);
    try {
      const r = await asJson(await fetch(`${apiUrl}/admin/crowdstrike/test`, send('POST', form.clientSecret ? form : {})));
      setTestNote({ ok: true, text: t('pages.endpointCoverage.connected', { defaultValue: 'Connected to CrowdStrike ({{hosts}} hosts)', hosts: r.hosts ?? '?' }) });
    } catch (e: any) {
      setTestNote({ ok: false, text: e.message });
    } finally {
      setBusy(null);
    }
  };

  const sync = () =>
    run('sync', async () => {
      const r = await asJson(await fetch(`${apiUrl}/admin/crowdstrike/sync`, send('POST', {})));
      setConfig(r.config);
      await loadReport();
      return t('pages.endpointCoverage.synced', { defaultValue: 'Synced {{hosts}} hosts', hosts: r.result.hosts });
    });

  const exportCsv = () =>
    run('export', async () => {
      const res = await fetch(`${apiUrl}/admin/endpoints/coverage?format=csv${filter === 'all' ? '' : `&status=${filter}`}`);
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = 'agenti5-endpoint-coverage.csv';
      a.click();
      URL.revokeObjectURL(url);
      return t('pages.endpointCoverage.exported', 'Exported');
    });

  // Green: last sync succeeded and is recent. Red: last sync failed, or nothing has synced for 3 intervals. Grey: not set up, paused, or waiting for the first sync.
  const status = (() => {
    if (!config) return { state: 'unknown' as const, label: t('pages.endpointCoverage.statusNotConnected', 'Not connected') };
    if (!config.enabled) return { state: 'unknown' as const, label: t('pages.endpointCoverage.statusPaused', 'Paused') };
    if (!config.lastSyncAt) return { state: 'unknown' as const, label: t('pages.endpointCoverage.statusWaiting', 'Waiting for first sync') };
    const ageMin = (Date.now() - config.lastSyncAt) / 60000;
    if (config.lastStatus && config.lastStatus !== 'ok') return { state: 'unhealthy' as const, label: t('pages.endpointCoverage.statusFailed', 'Sync failed') };
    if (ageMin > config.syncIntervalMin * 3) return { state: 'unhealthy' as const, label: t('pages.endpointCoverage.statusStale', 'Sync overdue') };
    return { state: 'healthy' as const, label: t('pages.endpointCoverage.statusConnected', 'Connected') };
  })();
  const intervalOptions = INTERVALS.some(([m]) => m === form.syncIntervalMin) ? INTERVALS : [...INTERVALS, [form.syncIntervalMin, `Every ${form.syncIntervalMin} min`] as [number, string]];

  const rows = (report?.rows || []).filter((r) => filter === 'all' || r.coverage === filter);
  const s = report?.summary;
  const when = (v: string | number | null | undefined) => (v ? parseServerDate(typeof v === 'number' ? new Date(v).toISOString() : v).toLocaleString() : '-');
  const label = (c: Coverage) => t(`pages.endpointCoverage.state.${c}`, c);

  const tile = (key: 'all' | Coverage, title: string, value: number | string, hint?: string) => (
    <button
      key={key}
      className={`ent-card${filter === key ? ' active' : ''}`}
      style={{ textAlign: 'left', cursor: 'pointer', padding: 14, border: filter === key ? '1px solid var(--accent, #2dd4bf)' : undefined }}
      onClick={() => setFilter(key)}
    >
      <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{title}</div>
      <div style={{ fontSize: 26, fontWeight: 700 }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{hint}</div>}
    </button>
  );

  return (
    <div className="page-content">
      <div className="page-title">
        <div>
          <h1>{t('pages.endpointCoverage.title', 'Endpoint coverage')}</h1>
          <p className="subtitle">
            {t('pages.endpointCoverage.subtitle', 'Where the Agenti5 agent is installed and where it is not, measured against your CrowdStrike host inventory.')}
          </p>
        </div>
        <div className="ent-toolbar" style={{ margin: 0, alignItems: 'center' }}>
          {loaded && (
            <span title={config?.lastStatus && config.lastStatus !== 'ok' ? config.lastStatus : undefined}>
              <StatusPulse state={status.state} label={status.label} />
            </span>
          )}
          {config?.lastSyncAt && (
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
              {t('pages.endpointCoverage.lastSync', { defaultValue: 'Last sync {{when}}', when: when(config.lastSyncAt) })}
            </span>
          )}
          <button className="ent-btn" onClick={() => { setTestNote(null); setShowSettings(true); }}>
            <Icon name="settings" /> {t('pages.endpointCoverage.settings', 'Settings')}
          </button>
          <button className="ent-btn primary" disabled={!canWrite || busy !== null || !config} onClick={sync}>
            <Icon name="refresh" /> {busy === 'sync' ? t('pages.endpointCoverage.syncing', 'Syncing...') : t('pages.endpointCoverage.syncNow', 'Sync now')}
          </button>
        </div>
      </div>

      {error && <div className="notice error"><Icon name="warning" /> {error}</div>}
      {note && <div className="notice"><Icon name="checkCircle" /> {note}</div>}

      {showSettings && (
        <Modal
          title={t('pages.endpointCoverage.connection', 'CrowdStrike connection')}
          icon="server"
          onClose={() => busy === null && setShowSettings(false)}
          maxWidth={520}
          footer={
            <>
              <button className="ent-btn ghost" onClick={() => setShowSettings(false)} disabled={busy !== null}>{t('pages.endpointCoverage.cancel', 'Cancel')}</button>
              <button className="ent-btn" disabled={!canWrite || busy !== null || (!form.clientSecret && !config?.hasSecret)} onClick={test}>
                {busy === 'test' ? t('pages.endpointCoverage.testing', 'Testing...') : t('pages.endpointCoverage.test', 'Test connection')}
              </button>
              <button className="ent-btn primary" disabled={!canWrite || busy !== null || !form.clientId || (!form.clientSecret && !config?.hasSecret)} onClick={save}>
                <Icon name="check" /> {t('pages.endpointCoverage.save', 'Save')}
              </button>
            </>
          }
        >
          {testNote && (
            <div className={`notice${testNote.ok ? '' : ' error'}`} style={{ marginBottom: 12 }}>
              <Icon name={testNote.ok ? 'checkCircle' : 'warning'} /> {testNote.text}
            </div>
          )}
          <div className="ent-field">
            <label>{t('pages.endpointCoverage.cloud', 'Falcon cloud')}</label>
            <select className="ent-input" value={form.cloud} disabled={!canWrite} onChange={(e) => setForm({ ...form, cloud: e.target.value })}>
              {clouds.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="ent-field">
            <label>{t('pages.endpointCoverage.clientId', 'API client ID')}</label>
            <input className="ent-input" value={form.clientId} disabled={!canWrite} autoComplete="off" onChange={(e) => setForm({ ...form, clientId: e.target.value })} />
          </div>
          <div className="ent-field">
            <label>{t('pages.endpointCoverage.clientSecret', 'API client secret')}</label>
            <input
              className="ent-input"
              type="password"
              autoComplete="new-password"
              value={form.clientSecret}
              disabled={!canWrite}
              placeholder={config?.hasSecret ? t('pages.endpointCoverage.secretKept', 'Saved - leave blank to keep') : ''}
              onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
            />
            <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 4 }}>
              {t('pages.endpointCoverage.secretHint', 'Needs the Hosts: Read scope. Stored encrypted; never shown again.')}
            </div>
          </div>
          <div className="ent-field">
            <label>{t('pages.endpointCoverage.interval', 'Refresh the host list')}</label>
            <select className="ent-input" value={form.syncIntervalMin} disabled={!canWrite} onChange={(e) => setForm({ ...form, syncIntervalMin: Number(e.target.value) })}>
              {intervalOptions.map(([m, l]) => <option key={m} value={m}>{l}</option>)}
            </select>
            <div style={{ fontSize: 11, color: 'var(--text-2)', marginTop: 4 }}>
              {t('pages.endpointCoverage.intervalHint', 'The host list changes slowly. Use Sync now after installing agents.')}
            </div>
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--text)' }}>
            <input type="checkbox" checked={form.enabled} disabled={!canWrite} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            {t('pages.endpointCoverage.enabled', 'Refresh automatically')}
          </label>
        </Modal>
      )}

      {s && s.total > 0 ? (
        <>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', marginBottom: 16 }}>
            {tile('all', t('pages.endpointCoverage.allHosts', 'All CrowdStrike hosts'), s.total, `${s.coveragePct}% ${t('pages.endpointCoverage.covered', 'covered')}`)}
            {tile('installed', label('installed'), s.installed)}
            {tile('silent', label('silent'), s.silent, t('pages.endpointCoverage.silentHint', 'Agent not checking in'))}
            {tile('missing', label('missing'), s.missing, t('pages.endpointCoverage.missingHint', 'No Agenti5 agent'))}
            {tile('inactive', label('inactive'), s.inactive, t('pages.endpointCoverage.inactiveHint', 'Not seen by CrowdStrike for 30+ days'))}
          </div>

          <div className="ent-toolbar">
            <button className="ent-btn" disabled={busy !== null} onClick={exportCsv}>
              <Icon name="download" /> {t('pages.endpointCoverage.exportCsv', 'Export CSV')}
            </button>
          </div>
          <div className="ent-table-wrap scroll">
            <table className="ent-table">
              <thead>
                <tr>
                  <th>{t('pages.endpointCoverage.colHost', 'Host')}</th>
                  <th>{t('pages.endpointCoverage.colPlatform', 'Platform')}</th>
                  <th>{t('pages.endpointCoverage.colState', 'Agenti5 agent')}</th>
                  <th>{t('pages.endpointCoverage.colFalconSeen', 'CrowdStrike last seen')}</th>
                  <th>{t('pages.endpointCoverage.colAgentSeen', 'Agent last seen')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={5} className="ent-empty">{t('pages.endpointCoverage.noRows', 'No hosts in this state')}</td></tr>}
                {rows.map((r) => (
                  <tr key={r.host.source_id}>
                    <td><strong>{r.host.hostname || r.host.source_id}</strong>{r.host.local_ip && <div style={{ fontSize: 11, color: 'var(--text-2)' }}>{r.host.local_ip}</div>}</td>
                    <td>{r.host.platform || '-'}</td>
                    <td><span className={`badge ${BADGE[r.coverage]}`}>{label(r.coverage)}</span></td>
                    <td>{when(r.host.last_seen)}</td>
                    <td>{when(r.agenti5?.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {report && report.unlisted.length > 0 && (
            <div className="ent-card" style={{ marginTop: 16 }}>
              <div className="ent-card-head">
                <Icon name="warning" /> {t('pages.endpointCoverage.unlistedTitle', { defaultValue: '{{n}} Agenti5 agents are not in the CrowdStrike inventory', n: report.unlisted.length })}
              </div>
              <div className="ent-card-body" style={{ fontSize: 12, color: 'var(--text-2)' }}>
                {t('pages.endpointCoverage.unlistedHint', 'These machines run the agent but CrowdStrike does not list them: unmanaged devices, or a hostname that does not match.')}
                <div style={{ marginTop: 8, color: 'var(--text)' }}>{report.unlisted.map((u) => u.hostname || u.endpoint_id).join(', ')}</div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="ent-card"><div className="ent-card-body ent-empty" style={{ display: 'grid', gap: 12, justifyItems: 'center', padding: 40 }}>
          <div>
            {config
              ? t('pages.endpointCoverage.emptyConfigured', 'No hosts synced yet. Select Sync now to pull the CrowdStrike inventory.')
              : t('pages.endpointCoverage.emptyUnconfigured', 'Connect CrowdStrike to compare its host inventory with the endpoints running Agenti5.')}
          </div>
          {!config && canWrite && (
            <button className="ent-btn primary" onClick={() => { setTestNote(null); setShowSettings(true); }}>
              <Icon name="plus" /> {t('pages.endpointCoverage.connect', 'Connect CrowdStrike')}
            </button>
          )}
        </div></div>
      )}
    </div>
  );
};

export default EndpointCoverage;
